'use client';

import React from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { INavigationLink } from '@/.generated';
import { usePageReferenceLink } from '@/lib/hooks/usePageReferenceUrl';
import { getCSLPAttributes } from '@/utils/type-guards';
import { useGlobalLabels } from '@/context/GlobalLabelContext';
import { setLanguagePreference } from '@/app/actions/language';
import { DEFAULT_LOCALE } from '@/constants/locales';
import { trackAdobeClick } from '@/utils/adobe/click';
import { localizeHref } from '@/utils/localize-href';
import {
  useRedirectBaseUrl,
  applyRedirectBaseUrl,
  useAbsoluteUrls,
} from '@/web-component/redirect-base-url-context';
import { useMyAccountType, resolveOrderUrl } from '@/web-component/my-account-type-context';
import ImageWrapper from '../ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';
import PlainTextWrapper from '../PlainTextWrapper/PlainTextWrapper';

const ES_LANG_CODE = 'es';

export interface NavigationLinkWrapperProps {
  navigationLink: INavigationLink;
  clickLocation?: string;
  label?: string;
  className?: string;
  siteSection?: string;
  shouldRenderNewTabIcon?: boolean;
  shouldRenderEngOnlyIcon?: boolean;
}

export const NavigationLinkWrapper = ({
  navigationLink,
  clickLocation,
  label,
  className,
  siteSection,
  children,
  shouldRenderNewTabIcon = true,
  shouldRenderEngOnlyIcon = true,
  ...rest
}: React.PropsWithChildren<NavigationLinkWrapperProps>) => {
  const { link, open_in_new_window, page_reference, english_only_link, $ } = navigationLink;

  // Resolve href + title from the page reference; V2 nav links often supply only a
  // page_reference, so the referenced page's title becomes the label fallback.
  const { href: resolvedHref, title: resolvedTitle } = usePageReferenceLink(
    page_reference,
    link?.href,
    link?.title
  );

  const linkLabel = label || link?.title || resolvedTitle;
  const { globalLabels } = useGlobalLabels();
  const params = useParams();
  const currentLocale = (params?.locale as string | undefined) ?? DEFAULT_LOCALE;

  const newTab = open_in_new_window;

  const engOnlyLink = english_only_link;
  const engOnlyIcon = globalLabels.english_only_identifier_icon;

  // Page-reference hrefs are already locale-resolved inside usePageReferenceUrl.
  // Raw `link.href` is locale-agnostic — localize it here so Spanish internal
  // nav lands on /es/..., except for english-only links (always English target).
  const localizedHref =
    page_reference?.length || engOnlyLink
      ? resolvedHref
      : localizeHref(resolvedHref, currentLocale);

  const redirectBaseUrl = useRedirectBaseUrl();
  // Only prefix root-relative paths when the consumer has opted in via useabsoluteurls="true".
  const shouldAbsolutify = useAbsoluteUrls();
  const accountType = useMyAccountType();
  // Rewrite the order URL when the consuming app supplies a myAccountType attribute.
  const rewrittenHref = resolveOrderUrl(localizedHref, accountType);
  // Apply the base-URL prefix only when useAbsoluteUrls is enabled.
  const href = applyRedirectBaseUrl(rewrittenHref, shouldAbsolutify ? redirectBaseUrl : undefined) as string;

  if (!linkLabel || !href) return null;

  // When the current session is Spanish and the link is flagged as
  // English-only, switch the language preference to English before navigating
  // so the target page renders in English rather than attempting a Spanish
  // variant that doesn't exist.
  const shouldSwitchToEnglish = engOnlyLink && currentLocale === ES_LANG_CODE;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackAdobeClick({ clickName: linkLabel, clickLocation }, e);
    if (!shouldSwitchToEnglish || newTab) return;
    e.preventDefault();
    void setLanguagePreference(DEFAULT_LOCALE, href);
  };

  return (
    <Link
      href={href}
      className={className}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
      aria-label={newTab ? `${linkLabel} (Opens in a new tab)` : linkLabel}
      onClick={handleClick}
      // {...analyticsProps}
      {...getCSLPAttributes($?.link)}
      {...rest}
    >
      {children || <PlainTextWrapper content={linkLabel} isDangerouslySetInnerHTML />}
      {shouldRenderEngOnlyIcon && engOnlyLink && engOnlyIcon?.url && (
        <span aria-hidden="true" className="ms-2.5 w-fit inline-block">
          <ImageWrapper
            image={toEnhancedImage(engOnlyIcon, {
              alternateText: engOnlyIcon.title,
              fallbackWidth: 16,
              fallbackHeight: 16,
            })}
            wrapperClassName="w-fit h-fit"
            imageClassName="h-[0.8rem] w-auto"
          />
        </span>
      )}
      {shouldRenderNewTabIcon && newTab && (
        <span aria-hidden="true" className="ms-2.5">
          <i className="fa-solid fa-arrow-up-right text-secondary"></i>
        </span>
      )}
    </Link>
  );
};
